/**
 * Frontend ops utilities for error capture and metrics
 * In-memory ring buffers plus beacon POSTs to the API (`/api/ops/ingest/*`) for server-side ops.
 */

import { apiFetch } from '@/lib/api-fetch';

export type ErrorLevel = 'error' | 'warn' | 'fatal';

export interface ErrorContext {
  route?: string;
  userId?: string;
  userAgent?: string;
  meta?: Record<string, any>;
}

interface CapturedError {
  id: string;
  ts: string;
  level: ErrorLevel;
  message: string;
  stack: string | null;
  route: string | null;
  user_id: string | null;
  meta: Record<string, any> | null;
  fingerprint: string;
}

interface PageView {
  path: string;
  ts: string;
  userAgent: string;
  referrer: string | null;
  userId: string | null;
}

// In-memory storage (ring buffer)
const MAX_MEMORY_ERRORS = 500;
const memoryErrors: CapturedError[] = [];

// In-memory page views (last 1000)
const MAX_PAGE_VIEWS = 1000;
const pageViews: PageView[] = [];

// Unique users tracking (last 24h)
const uniqueUsers = new Set<string>();
const userSessions = new Map<string, { firstSeen: string; lastSeen: string; pageViews: number }>();

// In-memory counters
const metrics = {
  totalPageViews: 0,
  totalErrors: 0,
  uniqueUsers24h: 0,
  popularPages: new Map<string, number>(),
};

/**
 * Generate UUID (simple implementation)
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate MD5 hash (simple implementation)
 */
function generateMD5(input: string): string {
  // Simple hash function (not cryptographically secure, but good enough for fingerprinting)
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Normalize error to a consistent format
 */
function normalizeError(err: unknown): { message: string; stack: string | null } {
  if (err instanceof Error) {
    return {
      message: err.message || 'Unknown error',
      stack: err.stack || null,
    };
  }
  if (typeof err === 'string') {
    return { message: err, stack: null };
  }
  return { message: String(err), stack: null };
}

/**
 * Generate a simple fingerprint for error grouping
 */
function generateFingerprint(message: string, stack?: string): string {
  const input = stack ? `${message}:${stack.split('\n')[0]}` : message;
  return generateMD5(input);
}

/**
 * Get or create user ID from session
 */
function getUserId(): string {
  if (typeof window === 'undefined') return 'server';
  
  // Try to get from localStorage
  try {
    let userId = localStorage.getItem('ops_user_id');
    if (!userId) {
      userId = generateUUID();
      localStorage.setItem('ops_user_id', userId);
    }
    return userId;
  } catch (err) {
    // localStorage might not be available
    return 'anonymous';
  }
}

/**
 * Capture error to memory
 */
export function captureError(
  err: unknown,
  level: ErrorLevel = 'error',
  context: ErrorContext = {}
): void {
  try {
    const { message, stack } = normalizeError(err);
    const fingerprint = generateFingerprint(message, stack || undefined);
    
    // Truncate stack to 8k chars
    const truncatedStack = stack && stack.length > 8000 ? stack.substring(0, 8000) : stack;
    
    // Sanitize metadata (remove sensitive data)
    const safeMeta: Record<string, any> = {};
    if (context.meta) {
      for (const [key, value] of Object.entries(context.meta)) {
        // Skip sensitive keys
        if (['password', 'secret', 'token', 'key', 'authorization'].some(s => key.toLowerCase().includes(s))) {
          continue;
        }
        // Only allow safe types
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
          safeMeta[key] = value;
        }
      }
    }

    const userId = context.userId || (typeof window !== 'undefined' ? getUserId() : null);

    const error: CapturedError = {
      id: generateUUID(),
      ts: new Date().toISOString(),
      level,
      message,
      stack: truncatedStack,
      route: context.route || null,
      user_id: userId,
      meta: Object.keys(safeMeta).length > 0 ? safeMeta : null,
      fingerprint,
    };

    // Add to ring buffer
    memoryErrors.push(error);
    if (memoryErrors.length > MAX_MEMORY_ERRORS) {
      memoryErrors.shift(); // Remove oldest error
    }

    // Update counters
    metrics.totalErrors++;

    void apiFetch('/api/ops/ingest/error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        stack: truncatedStack,
        level,
        route: context.route || null,
        userId,
        meta: Object.keys(safeMeta).length > 0 ? safeMeta : undefined,
      }),
    }).catch(() => {
      /* non-fatal */
    });
  } catch (err) {
    console.error('Failed to capture error:', err);
  }
}

/**
 * Track page view
 */
export function trackPageView(path: string, referrer?: string | null): void {
  try {
    const userId = typeof window !== 'undefined' ? getUserId() : 'server';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'server';
    
    const pageView: PageView = {
      path,
      ts: new Date().toISOString(),
      userAgent,
      referrer: referrer || null,
      userId,
    };

    // Add to ring buffer
    pageViews.push(pageView);
    if (pageViews.length > MAX_PAGE_VIEWS) {
      pageViews.shift(); // Remove oldest
    }

    // Update counters
    metrics.totalPageViews++;
    
    // Track unique users (24h window)
    const now = Date.now();
    
    // Clean old unique users
    if (uniqueUsers.size > 10000) {
      // Reset if too large (shouldn't happen, but safety)
      uniqueUsers.clear();
    }
    uniqueUsers.add(userId);
    
    // Update user session
    const session = userSessions.get(userId);
    if (session) {
      session.lastSeen = new Date().toISOString();
      session.pageViews++;
    } else {
      userSessions.set(userId, {
        firstSeen: new Date().toISOString(),
        lastSeen: new Date().toISOString(),
        pageViews: 1,
      });
    }
    
    // Track popular pages
    const count = metrics.popularPages.get(path) || 0;
    metrics.popularPages.set(path, count + 1);
    
    // Clean old sessions (older than 24h)
    for (const [uid, sess] of userSessions.entries()) {
      const lastSeen = new Date(sess.lastSeen).getTime();
      if (now - lastSeen > 24 * 60 * 60 * 1000) {
        userSessions.delete(uid);
        uniqueUsers.delete(uid);
      }
    }

    void apiFetch('/api/ops/ingest/page-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        path,
        referrer: referrer ?? null,
        userId,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }),
    }).catch(() => {
      /* non-fatal */
    });
  } catch (err) {
    console.error('Failed to track page view:', err);
  }
}

/**
 * Get errors from memory
 */
export function getErrors(since?: string, limit: number = 50): CapturedError[] {
  const maxLimit = Math.min(limit, MAX_MEMORY_ERRORS);
  
  // Filter by time if provided
  let errors = [...memoryErrors].reverse(); // Most recent first
  if (since) {
    errors = errors.filter(e => e.ts > since);
  }
  
  return errors.slice(0, maxLimit);
}

/**
 * Get metrics from memory
 */
export function getMetrics(window: '1h' | '24h' = '24h'): {
  window: string;
  pageViews: { total: number; unique: number };
  errors: { total: number; recent: number };
  popularPages: Array<{ path: string; views: number }>;
  users: { unique24h: number; activeSessions: number };
  lastError: {
    ts: string;
    message: string;
    route: string | null;
  } | null;
} {
  const now = Date.now();
  const windowMs = window === '1h' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
  const startTime = new Date(now - windowMs);

  // Filter page views by window
  const windowPageViews = pageViews.filter(pv => new Date(pv.ts).getTime() >= startTime.getTime());
  
  // Get unique users in window
  const windowUsers = new Set(windowPageViews.map(pv => pv.userId).filter(Boolean));
  
  // Filter errors by window
  const windowErrors = memoryErrors.filter(e => new Date(e.ts).getTime() >= startTime.getTime());
  
  // Get last error
  const lastError = windowErrors.length > 0
    ? windowErrors[windowErrors.length - 1] // Most recent
    : null;

  // Get popular pages (top 10)
  const popularPagesArray = Array.from(metrics.popularPages.entries())
    .map(([path, views]) => ({ path, views }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10);

  // Count active sessions (users with activity in last hour)
  const oneHourAgo = now - 60 * 60 * 1000;
  const activeSessions = Array.from(userSessions.values()).filter(
    sess => new Date(sess.lastSeen).getTime() >= oneHourAgo
  ).length;

  return {
    window,
    pageViews: {
      total: windowPageViews.length,
      unique: windowUsers.size,
    },
    errors: {
      total: metrics.totalErrors,
      recent: windowErrors.length,
    },
    popularPages: popularPagesArray,
    users: {
      unique24h: uniqueUsers.size,
      activeSessions,
    },
    lastError: lastError ? {
      ts: lastError.ts,
      message: lastError.message,
      route: lastError.route,
    } : null,
  };
}

/**
 * Clear old data (optional cleanup)
 */
export function clearOldData(maxAgeMs: number = 24 * 60 * 60 * 1000): void {
  const cutoff = new Date(Date.now() - maxAgeMs).toISOString();
  
  // Remove old errors
  for (let i = memoryErrors.length - 1; i >= 0; i--) {
    if (memoryErrors[i].ts < cutoff) {
      memoryErrors.splice(i, 1);
    }
  }
  
  // Remove old page views
  for (let i = pageViews.length - 1; i >= 0; i--) {
    if (pageViews[i].ts < cutoff) {
      pageViews.splice(i, 1);
    }
  }
}

// Auto-cleanup every hour (server-side only)
if (typeof setInterval !== 'undefined' && typeof window === 'undefined') {
  setInterval(() => {
    clearOldData(24 * 60 * 60 * 1000); // Keep last 24h
  }, 60 * 60 * 1000);
}

